/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */
import io from 'socket.io-client';
import { API } from '../store/utils/apiUtils/api';

// As the socketAPI extends the API class, it inherits all the API properties (i.e. _token, _organization etc)
export default class socketAPI extends API {
  constructor(host) {
    super();
    this.myHost = host;
    this.socket = null;
    this._eventMap = {};
  }

  /**
   * Function to establish socket connection utilizing socket.io method connect
   * Upon connection th we also add event listeners waiting in the eventObject
   * Connect should only be run on init, or after a manual disconnect.
   * If the connection is lost on its own (ie bad network or livechat sercer is down),
   * we should NOT manually try to reconnect or add again the same event listeners as
   * this is done by socket.io automatically.
   *
   * @param {*} token
   * @param {*} organizationId
   * @param {function} onConnectError
   * @param {function} onReconnect
   * @returns {void}
   */
  connect(token, organizationId, onConnectError, onReconnect) {
    if (this.socket) {
      this.disconnect();
    }
    this.socket = io.connect(this.myHost, {
      transports: ['websocket', 'polling', 'flashsocket'],
      auth: {
        token: `Bearer ${token}`
      },
      query: `organizationId=${organizationId}`
    });

    Object.keys(this._eventMap).map(key => this.socket.on(key, this._eventMap[key]));

    this.socket.on('connect_error', (error) => {
      onConnectError(error);
    });

    this.socket.io.on('reconnect', () => {
      onReconnect();
    });
  }

  /**
   * Function to remove a socket connection utilizing socket.io method disconnect
   *
   * @returns {void}
   */
  disconnect() {
    this.socket.disconnect(() => {
      this.socket = null;
    });
  }

  /**
   * Function to emit an event to the socket utilizing socket.io method emit
   *
   * @param {string} event
   * @param {*} data
   * @returns {Promise}
   */
  emit(event, data) {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        return reject('No socket connection.');
      }

      return this.socket.emit(event, data, (response) => {
        // Response is the optional callback that you can use with socket.io in every request. See 1 above.
        if (response.error) {
          console.error(response.error);
          return reject(response.error);
        }

        return resolve(response);
      });
    });
  }

  on(event, fun) {
    // No promise is needed here, but we're expecting one in the middleware.
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        return reject('No socket connection.');
      }
      this.socket.on(event, fun);
      resolve();
    });
  }

  /**
   * Function to apply a new event listener to the socket instance.
   * We keep the event listeners to an object for the case that the connection is not established.
   * If we have a socket.io instance active, then we also add the event listener at the same time.
   *
   * @param {string} event
   * @param {function} fun
   * @returns {void}
   */
  applyEvent(event, fun) {
    this._eventMap[event] = fun;
    if (this.socket) {
      this.socket.on(event, fun);
    }
  }
}
